import { isTripOrder, type Order, type OrderStatus } from "../../../entities/order";
import type { Trip } from "../../../entities/trip";
import { listTrips } from "../../trips/services/tripService";
import {
  cancelOrder,
  getOrder,
  listOrders,
  transitionOrder,
  validateOrderItem,
  type OrderTransitionTarget,
  type ValidateItemPayload,
} from "../../orders/services/orderService";

// Côté agent, une "mission" = une `Order` STANDALONE que l'agent exécute via les
// endpoints /orders/*.
//
// ⚠️ Depuis l'ajout des courses multi-arrêts (Trip), une `Order` peut être une
// ÉTAPE de course (`trip_id != null`). Son cycle de vie est alors piloté par le
// Trip (POST /trips/{id}/stops/{stopIndex}) et NON par /orders/{id}/transition —
// transitionner une commande-étape ici entrerait en conflit avec la cascade
// serveur du Trip. On exclut donc ces commandes des missions standalone : elles
// remontent via `listAgentActiveTrips` et sont exécutées par la feature `trips`.

const AGENT_ACTIVE_STATUSES: OrderStatus[] = [
  "AGENT_ASSIGNED",
  "AGENT_EN_ROUTE",
  "PURCHASING",
  "PURCHASED",
  "DELIVERING",
];

const TRIP_ACTIVE_STATUSES = [
  "AGENT_ASSIGNED",
  "AGENT_EN_ROUTE",
  "IN_PROGRESS",
  "AWAITING_CLIENT_APPROVAL", // Bouclier Qualité — l'agent attend la réponse client
  "DELIVERING",
] as const;

const ACTIVE_TRIP_STATUS_SET = new Set<string>(TRIP_ACTIVE_STATUSES);

// ─── Listes ───────────────────────────────────────────────────────────────────

// Missions standalone ouvertes assignées à l'agent (hors étapes de course).
export async function listAgentActiveMissions(agentId: string): Promise<Order[]> {
  // Le endpoint /orders n'accepte qu'un seul `status` à la fois → on aggrège.
  const responses = await Promise.all(
    AGENT_ACTIVE_STATUSES.map((status) =>
      listOrders({ agent_id: agentId, status, per_page: 20 }),
    ),
  );
  // Dédoublonne par id (sécurité — un même order ne devrait sortir qu'une fois)
  const map = new Map<string, Order>();
  for (const res of responses) for (const o of (res.data ?? [])) if (o.id) map.set(o.id, o);
  // Exclut les commandes-étapes de course (pilotées via tripService, pas /orders).
  const standalone = Array.from(map.values()).filter((o) => !isTripOrder(o));
  // Tri par created_at desc
  return standalone.sort((a, b) => {
    const at = a.created_at ? Date.parse(a.created_at) : 0;
    const bt = b.created_at ? Date.parse(b.created_at) : 0;
    return bt - at;
  });
}

// ⚠️ OBSOLÈTE depuis le contrat du 2026-08-28 — conservée pour les appels qui
// disposent VRAIMENT d'un rôle admin. Pour une Main, `?agent_id=` est
// silencieusement ignoré (l'identité vient du jeton) : le filtrage n'a jamais
// eu lieu. Les écrans passent désormais par `listMyAgentTrips`.
//
// Courses multi-arrêts actives assignées à l'agent CONNECTÉ.
//
// Endpoint NOMINAL du contrat : GET /trips?agent_id=<Agent.id> (l'id du PROFIL
// agent — les données réelles prouvent que trip.agent_id porte cette valeur).
// AUCUN fallback (politique projet : les vraies réponses API doivent remonter) —
// si le backend ne renvoie pas les courses de l'agent ici, c'est un bug backend
// à corriger côté serveur, pas à contourner côté app.
// Le statut actif est filtré côté client (le param `status` n'accepte qu'une valeur).
export async function listAgentActiveTrips(agentId: string): Promise<Trip[]> {
  const trips  = await listTrips({ agent_id: agentId });
  const active = (trips ?? []).filter(
    (t) => t.status != null && ACTIVE_TRIP_STATUS_SET.has(t.status),
  );
  return active.sort((a, b) => {
    const at = a.created_at ? Date.parse(a.created_at) : 0;
    const bt = b.created_at ? Date.parse(b.created_at) : 0;
    return bt - at;
  });
}

export async function listAgentMissionHistory(agentId: string): Promise<Order[]> {
  const res = await listOrders({ agent_id: agentId, status: "DELIVERED", per_page: 50 });
  return res.data ?? [];
}

export async function getMissionDetail(orderId: string): Promise<Order> {
  return getOrder(orderId);
}

// ─── Transitions de cycle ─────────────────────────────────────────────────────

export async function acceptMission(orderId: string): Promise<Order> {
  return transitionOrder(orderId, "AGENT_EN_ROUTE", "Mission acceptée");
}

export async function startPurchasing(orderId: string): Promise<Order> {
  return transitionOrder(orderId, "PURCHASING", "Achats en cours");
}

export async function markPurchased(orderId: string): Promise<Order> {
  return transitionOrder(orderId, "PURCHASED", "Achats terminés");
}

export async function startDelivering(orderId: string): Promise<Order> {
  return transitionOrder(orderId, "DELIVERING", "En route vers le client");
}

export async function deliverMission(orderId: string): Promise<Order> {
  return transitionOrder(orderId, "DELIVERED", "Livré au client");
}

export async function disputeMission(orderId: string, reason?: string): Promise<Order> {
  return transitionOrder(orderId, "DISPUTED", reason);
}

export async function cancelMission(orderId: string, reason?: string): Promise<Order> {
  return cancelOrder(orderId, reason);
}

// Pass-through pour la checklist (validation d'un item)
export async function validateMissionItem(
  orderId: string,
  itemId:  string,
  payload: ValidateItemPayload,
): Promise<void> {
  await validateOrderItem(orderId, itemId, payload);
}

export type { OrderTransitionTarget };
